// ---------------------------------------------------------------------------
// AppLock — Public API entry point
// ---------------------------------------------------------------------------
//
// This module assembles all subsystems (config, state machine, event bus,
// password manager, crypto, storage, idle detection, and UI) into a
// single cohesive API.
//
// Usage:
//   import { AppLock } from 'applock';
//   const lock = AppLock.init({ mode: 'encrypted' });
//
// ---------------------------------------------------------------------------
import { resolveConfig } from './core/config.js';
import { EventBus } from './core/event-bus.js';
import { StateMachine } from './core/state-machine.js';
import { PasswordManager } from './auth/password-manager.js';
import { KeyLifecycle } from './crypto/key-lifecycle.js';
import { PassthroughStorage } from './storage/passthrough.js';
import { EncryptedStorage } from './storage/encrypted.js';
import { LocalStorageBackend } from './storage/storage-backend.js';
import { IDBBackend } from './storage/idb-backend.js';
import { IDBRecordAdapter } from './storage/idb-record-adapter.js';
import { IdleDetector } from './idle/idle-detector.js';
import { LockScreenUI } from './ui/lock-screen.js';
export { AppLockCryptoError } from './crypto/crypto-adapter.js';
export { IDBBackend } from './storage/idb-backend.js';
export { IDBRecordAdapter } from './storage/idb-record-adapter.js';
export { LocalStorageBackend } from './storage/storage-backend.js';
// ---------------------------------------------------------------------------
// AppLock implementation
// ---------------------------------------------------------------------------
class AppLockImpl {
    constructor(userConfig) {
        this.encryptedStorage = null;
        this.idbRecordAdapter = null;
        this.lockScreenUI = null;
        this.beforeUnloadHandler = null;
        this.config = resolveConfig(userConfig);
        this.bus = new EventBus();
        this.sm = new StateMachine(this.bus);
        this.passwordManager = new PasswordManager(this.config.pbkdf2Iterations);
        this.keyLifecycle = new KeyLifecycle();
        // Create the raw storage backend for the flat key-value API
        if (this.config.storageBackend === 'indexedDB') {
            this.storageBackendImpl = new IDBBackend(this.config.idbDatabaseName, this.config.idbStoreName);
        }
        else {
            this.storageBackendImpl = new LocalStorageBackend();
        }
        // Create flat storage adapter based on mode
        if (this.config.mode === 'encrypted') {
            this.encryptedStorage = new EncryptedStorage(this.keyLifecycle, this.config, this.storageBackendImpl);
            this.storageAdapter = this.encryptedStorage;
        }
        else {
            this.storageAdapter = new PassthroughStorage(this.storageBackendImpl);
        }
        // Create record-level IDB adapter when using IndexedDB backend
        if (this.config.storageBackend === 'indexedDB') {
            this.idbRecordAdapter = new IDBRecordAdapter(this.keyLifecycle, this.config);
        }
        // Idle detector
        this.idleDetector = new IdleDetector(this.config, () => this.lock('idle'), this.config.lockOnHide ? () => this.lock('visibility') : undefined);
        // Lock screen UI (optional)
        if (this.config.ui) {
            this.lockScreenUI = new LockScreenUI(this.config, this.bus);
        }
        // Tab close handler for encrypted mode
        if (this.config.mode === 'encrypted') {
            this.beforeUnloadHandler = () => {
                this.keyLifecycle.release();
            };
            window.addEventListener('beforeunload', this.beforeUnloadHandler);
        }
        // Initialize state based on whether a password exists
        this.initializeState();
    }
    // -----------------------------------------------------------------------
    // State
    // -----------------------------------------------------------------------
    get state() {
        return this.sm.state;
    }
    get isLocked() {
        return this.sm.isLocked;
    }
    get isConfigured() {
        return this.passwordManager.isConfigured();
    }
    // -----------------------------------------------------------------------
    // Auth
    // -----------------------------------------------------------------------
    async setup(password) {
        const key = await this.passwordManager.setup(password);
        if (this.config.mode === 'encrypted') {
            this.keyLifecycle.hold(key);
            // Migrate any existing cleartext data to encrypted
            if (this.encryptedStorage) {
                await this.encryptedStorage.migrateToEncrypted(key);
            }
            if (this.idbRecordAdapter) {
                await this.idbRecordAdapter.migrateToEncrypted(key);
            }
        }
        // Transition: uninitialized → locked → unlock
        this.sm.transition('locked', 'init');
        this.bus.emit('setup');
        // Auto-unlock after setup so the user can start using the app
        this.sm.transition('unlocking');
        this.sm.transition('unlocked');
        this.bus.emit('unlock');
        // Start idle detection
        this.idleDetector.start();
        // Hide lock screen
        if (this.lockScreenUI) {
            this.lockScreenUI.hide();
        }
    }
    async unlock(password) {
        this.sm.transition('unlocking');
        const key = await this.passwordManager.verify(password);
        if (!key) {
            this.sm.transition('locked', this.sm.lockReason);
            return false;
        }
        // Hold the key in memory for encrypted mode
        if (this.config.mode === 'encrypted') {
            this.keyLifecycle.hold(key);
        }
        this.sm.transition('unlocked');
        this.bus.emit('unlock');
        // Start idle detection
        this.idleDetector.start();
        // Hide lock screen
        if (this.lockScreenUI) {
            this.lockScreenUI.hide();
        }
        return true;
    }
    lock(reason = 'manual') {
        if (this.sm.state === 'locked' || this.sm.state === 'uninitialized')
            return;
        // Stop idle detection
        this.idleDetector.stop();
        // Release encryption key from memory
        if (this.config.mode === 'encrypted') {
            this.keyLifecycle.release();
        }
        // Clear clipboard if configured
        if (this.config.clearClipboard) {
            navigator.clipboard?.writeText?.('').catch(() => {
                // Clipboard API may not be available
            });
        }
        this.sm.transition('locked', reason);
        this.bus.emit('lock', { reason });
        // Show lock screen
        if (this.lockScreenUI) {
            this.lockScreenUI.show((pw) => this.unlock(pw));
        }
    }
    async changePassword(current, next) {
        const { oldKey, newKey } = await this.passwordManager.changePassword(current, next);
        // Re-encrypt all data with the new key
        if (this.config.mode === 'encrypted') {
            if (this.encryptedStorage) {
                await this.encryptedStorage.reEncrypt(oldKey, newKey);
            }
            if (this.idbRecordAdapter) {
                await this.idbRecordAdapter.reEncrypt(oldKey, newKey);
            }
            this.keyLifecycle.hold(newKey);
        }
        this.bus.emit('password:change');
    }
    async removePassword(current) {
        const key = await this.passwordManager.remove(current);
        // Decrypt all data back to cleartext
        if (this.config.mode === 'encrypted') {
            if (this.encryptedStorage) {
                await this.encryptedStorage.migrateToCleartext(key);
            }
            if (this.idbRecordAdapter) {
                await this.idbRecordAdapter.migrateToCleartext(key);
            }
            this.keyLifecycle.release();
            // Switch flat storage adapter to passthrough
            this.storageAdapter = new PassthroughStorage(this.storageBackendImpl);
            this.encryptedStorage = null;
        }
        // Stop idle detection
        this.idleDetector.stop();
        this.sm.forceState('uninitialized');
        this.bus.emit('password:clear');
        // Hide lock screen
        if (this.lockScreenUI) {
            this.lockScreenUI.hide();
        }
    }
    // -----------------------------------------------------------------------
    // Storage
    // -----------------------------------------------------------------------
    get store() {
        return this.storageAdapter;
    }
    get idb() {
        return this.idbRecordAdapter;
    }
    // -----------------------------------------------------------------------
    // Events
    // -----------------------------------------------------------------------
    on(event, handler) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.bus.on(event, handler);
    }
    off(event, handler) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.bus.off(event, handler);
    }
    // -----------------------------------------------------------------------
    // Lifecycle
    // -----------------------------------------------------------------------
    destroy() {
        this.idleDetector.destroy();
        this.keyLifecycle.release();
        this.bus.clear();
        if (this.lockScreenUI) {
            this.lockScreenUI.destroy();
        }
        if (this.beforeUnloadHandler) {
            window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        }
        // Close IndexedDB connections
        if (this.storageBackendImpl && 'close' in this.storageBackendImpl) {
            this.storageBackendImpl.close();
        }
        if (this.idbRecordAdapter) {
            this.idbRecordAdapter.close();
        }
    }
    // -----------------------------------------------------------------------
    // Initialization
    // -----------------------------------------------------------------------
    initializeState() {
        if (this.passwordManager.isConfigured()) {
            // Password exists — start in locked state
            this.sm.forceState('locked');
            if (this.lockScreenUI) {
                this.lockScreenUI.show((pw) => this.unlock(pw));
            }
        }
        else {
            // No password — check if we should show setup
            // The app starts in uninitialized state. The consuming app
            // decides when to trigger setup (or the lock screen can show
            // setup automatically).
            this.sm.forceState('uninitialized');
            // Start idle detection even without a password
            // so that if setup is done later, it's already running
        }
    }
}
// ---------------------------------------------------------------------------
// Static factory
// ---------------------------------------------------------------------------
export const AppLock = {
    /**
     * Initialize AppLock with the given configuration.
     *
     * @example
     * // Tier 1: Visual lock only
     * const lock = AppLock.init({ mode: 'visual' });
     *
     * @example
     * // Tier 2: Full encryption (localStorage)
     * const lock = AppLock.init({
     *   mode: 'encrypted',
     *   idleTimeout: 300,
     *   encryptedKeys: ['calculations', 'client-data'],
     * });
     *
     * @example
     * // Tier 2: Record-level encryption in the app's IndexedDB
     * const lock = AppLock.init({
     *   mode: 'encrypted',
     *   storageBackend: 'indexedDB',
     *   idbDatabaseName: 'ascend-db',
     * });
     * // Then use: lock.idb.put('history', item), lock.idb.get('history', id), etc.
     */
    init(config) {
        return new AppLockImpl(config);
    },
};
export default AppLock;
//# sourceMappingURL=index.js.map